'use client'

import ConfirmModal from '@/components/confirm-modal'
import {
    MedicalEvolution,
    deleteEvolution,
    getEvolution,
    signEvolution,
} from '@/lib/api/prontuario/prontuario-api'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { useEffect, useState } from 'react'
import {
    MdArrowBack,
    MdCheck,
    MdCreate,
    MdDelete,
    MdEdit,
    MdMonitorHeart,
    MdPerson,
    MdSchedule,
    MdTrendingUp,
} from 'react-icons/md'
import { toast } from 'react-toastify'
import { useProntuarioNavigate, useProntuarioParams } from './prontuario-context'

function InfoItem({ label, value }: { label: string; value?: string | null }) {
    if (!value) return null
    return (
        <div>
            <p className="text-xs font-semibold text-base-content/60 mb-1">{label}</p>
            <p className="text-sm text-base-content whitespace-pre-wrap">{value}</p>
        </div>
    )
}

export default function EvolucaoDetailPage() {
    const navigate = useProntuarioNavigate()
    const { clientId, evolucaoId } = useProntuarioParams<{ clientId: string; evolucaoId: string }>()

    const [evolution, setEvolution] = useState<MedicalEvolution | null>(null)
    const [loading, setLoading] = useState(true)
    const [signing, setSigning] = useState(false)
    const [showSignModal, setShowSignModal] = useState(false)
    const [showDeleteModal, setShowDeleteModal] = useState(false)

    const basePath = `/person/clients/${clientId}/prontuario`

    useEffect(() => {
        if (evolucaoId) {
            loadEvolution()
        }
    }, [evolucaoId])

    const loadEvolution = async () => {
        if (!evolucaoId) return

        setLoading(true)
        try {
            const response = await getEvolution(parseInt(evolucaoId, 10))
            if (response.success && response.result) {
                setEvolution(response.result)
            } else {
                toast.error(response.error || 'Erro ao carregar evolução')
                navigate(`${basePath}/evolucoes`)
            }
        } catch {
            toast.error('Erro ao carregar evolução')
            navigate(`${basePath}/evolucoes`)
        } finally {
            setLoading(false)
        }
    }

    const confirmSign = async () => {
        if (!evolucaoId) return

        setSigning(true)
        try {
            const response = await signEvolution(parseInt(evolucaoId, 10))
            if (response.success) {
                toast.success('Evolução assinada com sucesso!')
                await loadEvolution()
            } else {
                toast.error(response.error || 'Erro ao assinar evolução')
            }
        } catch {
            toast.error('Erro ao assinar evolução')
        } finally {
            setSigning(false)
            setShowSignModal(false)
        }
    }

    const confirmDelete = async () => {
        if (!evolucaoId) return

        try {
            const response = await deleteEvolution(parseInt(evolucaoId, 10))
            if (response.success) {
                toast.success('Evolução excluída com sucesso!')
                navigate(`${basePath}/evolucoes`)
            } else {
                toast.error(response.error || 'Erro ao excluir evolução')
            }
        } catch {
            toast.error('Erro ao excluir evolução')
        }
    }

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">Carregando evolução...</p>
            </div>
        )
    }

    if (!evolution) return null

    const isSigned = !!evolution.signedAt
    const vs = evolution.vitalSigns

    return (
        <div className="flex flex-col gap-6 max-w-4xl mx-auto">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <button
                        onClick={() => navigate(`${basePath}/evolucoes`)}
                        className="btn btn-ghost btn-circle btn-sm"
                    >
                        <MdArrowBack className="text-xl" />
                    </button>
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-secondary/10 rounded-lg">
                            <MdTrendingUp className="text-2xl text-secondary" />
                        </div>
                        <div>
                            <div className="flex items-center gap-2">
                                <h1 className="text-xl font-bold text-base-content">
                                    Evolução #{evolution.id}
                                </h1>
                                {isSigned ? (
                                    <span className="badge badge-success gap-1">
                                        <MdCheck className="text-xs" />
                                        Assinada
                                    </span>
                                ) : (
                                    <span className="badge badge-warning">Pendente</span>
                                )}
                            </div>
                            <p className="text-sm text-base-content/60">
                                {format(new Date(evolution.evolutionDate), "dd 'de' MMMM 'de' yyyy 'às' HH:mm", { locale: ptBR })}
                            </p>
                        </div>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    {!isSigned && (
                        <>
                            <button
                                onClick={() => setShowSignModal(true)}
                                className="btn btn-success btn-sm gap-2"
                            >
                                <MdCreate className="text-lg" />
                                Assinar
                            </button>
                            <button
                                onClick={() => navigate(`${basePath}/evolucoes/${evolution.id}/editar`)}
                                className="btn btn-secondary btn-sm gap-2"
                            >
                                <MdEdit className="text-lg" />
                                Editar
                            </button>
                            <button
                                onClick={() => setShowDeleteModal(true)}
                                className="btn btn-error btn-outline btn-sm gap-2"
                            >
                                <MdDelete className="text-lg" />
                                Excluir
                            </button>
                        </>
                    )}
                </div>
            </div>

            {/* Professional */}
            {evolution.professional && (
                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6">
                    <div className="flex items-center gap-2 text-sm text-base-content">
                        <MdPerson className="text-base text-secondary" />
                        <span className="font-medium">
                            {evolution.professional.name}
                            {evolution.professional.registrationNumber && (
                                <span className="text-base-content/60">
                                    {' '}- {evolution.professional.councilName} {evolution.professional.registrationNumber}
                                </span>
                            )}
                        </span>
                    </div>
                    {isSigned && evolution.signedAt && (
                        <p className="text-xs text-success mt-2">
                            Assinado em {format(new Date(evolution.signedAt), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                        </p>
                    )}
                </div>
            )}

            {/* SOAP */}
            <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6">
                <h3 className="text-lg font-bold text-base-content mb-4">SOAP</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {evolution.subjective && (
                        <div className="bg-secondary/5 rounded-lg p-4 border border-secondary/10">
                            <p className="text-xs font-bold text-secondary mb-2">S - Subjetivo</p>
                            <p className="text-sm text-base-content whitespace-pre-wrap">{evolution.subjective}</p>
                        </div>
                    )}

                    {evolution.objective && (
                        <div className="bg-secondary/5 rounded-lg p-4 border border-secondary/10">
                            <p className="text-xs font-bold text-secondary mb-2">O - Objetivo</p>
                            <p className="text-sm text-base-content whitespace-pre-wrap">{evolution.objective}</p>
                        </div>
                    )}

                    {evolution.assessment && (
                        <div className="bg-accent/5 rounded-lg p-4 border border-accent/10">
                            <p className="text-xs font-bold text-accent mb-2">A - Avaliação</p>
                            <p className="text-sm text-base-content whitespace-pre-wrap">{evolution.assessment}</p>
                        </div>
                    )}

                    {evolution.plan && (
                        <div className="bg-info/5 rounded-lg p-4 border border-info/10">
                            <p className="text-xs font-bold text-info mb-2">P - Plano</p>
                            <p className="text-sm text-base-content whitespace-pre-wrap">{evolution.plan}</p>
                        </div>
                    )}
                </div>

                {!evolution.subjective && !evolution.objective && !evolution.assessment && !evolution.plan && (
                    <p className="text-sm text-base-content/50 italic">Nenhum registro SOAP preenchido</p>
                )}
            </div>

            {/* Vital Signs */}
            {vs && (vs.bloodPressure || vs.heartRate || vs.temperature || vs.oxygenSaturation || vs.respiratoryRate || vs.weight || vs.height || vs.painLevel || vs.bloodGlucose) && (
                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6">
                    <div className="flex items-center gap-2 mb-4">
                        <MdMonitorHeart className="text-xl text-error" />
                        <h3 className="text-lg font-bold text-base-content">Sinais Vitais</h3>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                        {vs.bloodPressure && (
                            <div className="bg-base-200/50 rounded-lg p-3 text-center">
                                <p className="text-xs font-semibold text-base-content/60 mb-1">Pressão Arterial</p>
                                <p className="text-lg font-bold text-base-content">{vs.bloodPressure}</p>
                                <p className="text-xs text-base-content/50">mmHg</p>
                            </div>
                        )}
                        {vs.heartRate && (
                            <div className="bg-base-200/50 rounded-lg p-3 text-center">
                                <p className="text-xs font-semibold text-base-content/60 mb-1">Freq. Cardíaca</p>
                                <p className="text-lg font-bold text-base-content">{vs.heartRate}</p>
                                <p className="text-xs text-base-content/50">bpm</p>
                            </div>
                        )}
                        {vs.temperature && (
                            <div className="bg-base-200/50 rounded-lg p-3 text-center">
                                <p className="text-xs font-semibold text-base-content/60 mb-1">Temperatura</p>
                                <p className="text-lg font-bold text-base-content">{vs.temperature}</p>
                                <p className="text-xs text-base-content/50">C</p>
                            </div>
                        )}
                        {vs.oxygenSaturation && (
                            <div className="bg-base-200/50 rounded-lg p-3 text-center">
                                <p className="text-xs font-semibold text-base-content/60 mb-1">SpO2</p>
                                <p className="text-lg font-bold text-base-content">{vs.oxygenSaturation}</p>
                                <p className="text-xs text-base-content/50">%</p>
                            </div>
                        )}
                        {vs.respiratoryRate && (
                            <div className="bg-base-200/50 rounded-lg p-3 text-center">
                                <p className="text-xs font-semibold text-base-content/60 mb-1">Freq. Respiratória</p>
                                <p className="text-lg font-bold text-base-content">{vs.respiratoryRate}</p>
                                <p className="text-xs text-base-content/50">irpm</p>
                            </div>
                        )}
                        {vs.weight && (
                            <div className="bg-base-200/50 rounded-lg p-3 text-center">
                                <p className="text-xs font-semibold text-base-content/60 mb-1">Peso</p>
                                <p className="text-lg font-bold text-base-content">{vs.weight}</p>
                                <p className="text-xs text-base-content/50">kg</p>
                            </div>
                        )}
                        {vs.height && (
                            <div className="bg-base-200/50 rounded-lg p-3 text-center">
                                <p className="text-xs font-semibold text-base-content/60 mb-1">Altura</p>
                                <p className="text-lg font-bold text-base-content">{vs.height}</p>
                                <p className="text-xs text-base-content/50">m</p>
                            </div>
                        )}
                        {vs.painLevel !== undefined && vs.painLevel !== null && (
                            <div className="bg-base-200/50 rounded-lg p-3 text-center">
                                <p className="text-xs font-semibold text-base-content/60 mb-1">Dor</p>
                                <p className="text-lg font-bold text-base-content">{vs.painLevel}</p>
                                <p className="text-xs text-base-content/50">/10</p>
                            </div>
                        )}
                        {vs.bloodGlucose && (
                            <div className="bg-base-200/50 rounded-lg p-3 text-center">
                                <p className="text-xs font-semibold text-base-content/60 mb-1">Glicemia</p>
                                <p className="text-lg font-bold text-base-content">{vs.bloodGlucose}</p>
                                <p className="text-xs text-base-content/50">mg/dL</p>
                            </div>
                        )}
                    </div>
                    {vs.weight && vs.height && vs.height > 0 && (
                        <div className="mt-4 bg-base-200/50 rounded-lg p-3 inline-block">
                            <p className="text-xs font-semibold text-base-content/60 mb-1">IMC</p>
                            <p className="text-sm font-bold text-base-content">
                                {(vs.weight / (vs.height * vs.height)).toFixed(1)} kg/m²
                            </p>
                        </div>
                    )}
                </div>
            )}

            {/* Exams Requested */}
            {evolution.examsRequested && (
                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6">
                    <h3 className="text-lg font-bold text-base-content mb-4">Exames Solicitados</h3>
                    <p className="text-sm text-base-content whitespace-pre-wrap">{evolution.examsRequested}</p>
                </div>
            )}

            {/* Return */}
            {(evolution.returnDate || evolution.returnNotes) && (
                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6">
                    <div className="flex items-center gap-2 mb-4">
                        <MdSchedule className="text-xl text-info" />
                        <h3 className="text-lg font-bold text-base-content">Retorno</h3>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {evolution.returnDate && (
                            <InfoItem
                                label="Data do Retorno"
                                value={format(new Date(evolution.returnDate), 'dd/MM/yyyy', { locale: ptBR })}
                            />
                        )}
                        <InfoItem label="Observações" value={evolution.returnNotes} />
                    </div>
                </div>
            )}

            {/* Footer info */}
            <div className="text-xs text-base-content/50 text-right pb-4">
                Criado em {format(new Date(evolution.createdAt), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                {' | '}
                Última atualização: {format(new Date(evolution.updatedAt), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
            </div>

            <ConfirmModal
                isOpen={showSignModal}
                onClose={() => setShowSignModal(false)}
                onConfirm={confirmSign}
                title="Assinar evolução"
                message="Deseja assinar digitalmente esta evolução? Após a assinatura, não será possível editar."
                confirmButtonText="Assinar"
                cancelButtonText="Cancelar"
                loading={signing}
            />

            <ConfirmModal
                isOpen={showDeleteModal}
                onClose={() => setShowDeleteModal(false)}
                onConfirm={confirmDelete}
                title="Excluir evolução"
                message="Tem certeza que deseja excluir esta evolução?"
                confirmButtonText="Excluir"
                cancelButtonText="Cancelar"
                confirmButtonClass="btn-error"
            />
        </div>
    )
}
